/**
 * Primitivas compartidas por todos los contratos: version, envoltura de error,
 * paginacion por cursor y tipos base validados con Zod.
 *
 * Los DTOs son la frontera entre la PWA y la API. Estan versionados: `API_VERSION`
 * viaja en la ruta (`/v1/...`) y `CONTRACT_VERSION` en la cabecera `X-Contract-Version`,
 * de modo que un cliente viejo pueda detectar que quedo desfasado sin romperse.
 */

import { z } from "zod";

export const API_VERSION = "v1";
export const CONTRACT_VERSION = "1.0.0";

// ---------------------------------------------------------------------------
// Primitivas
// ---------------------------------------------------------------------------

export const ulidSchema = z
  .string()
  .regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, "Identificador inválido");

/** Identificador aceptado por la API. Los seeds usan ULIDs reales. */
export const idSchema = ulidSchema;

export const instantSchema = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/,
    "Instante UTC inválido",
  );

export const operationalDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Fecha inválida (se espera AAAA-MM-DD)");

export const currencySchema = z.enum(["GTQ", "USD"]);

/**
 * Dinero en la unidad minima. Se transporta como ENTERO, nunca como decimal:
 * asi no hay redondeo del serializador JSON en el camino.
 */
export const moneySchema = z.object({
  minor: z.number().int().safe(),
  currency: currencySchema,
});
export type MoneyDto = z.infer<typeof moneySchema>;

export const positiveMoneySchema = moneySchema.refine(
  (value) => value.minor > 0,
  {
    message: "El importe debe ser mayor que cero",
  },
);

// ---------------------------------------------------------------------------
// Envoltura de error
// ---------------------------------------------------------------------------

/**
 * Todas las respuestas de error usan esta forma. `code` es funcional y estable
 * (el cliente decide con el); `message` es texto en espanol para el usuario.
 * `details` nunca incluye stack traces, SQL, secretos ni payloads completos.
 */
export const errorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.unknown()).optional(),
    /** Identificador de correlacion para rastrear el caso en los logs. */
    correlationId: z.string(),
  }),
});
export type ErrorResponse = z.infer<typeof errorResponseSchema>;

export const HTTP_STATUS_BY_CODE: Readonly<Record<string, number>> =
  Object.freeze({
    NO_AUTENTICADO: 401,
    SESION_EXPIRADA: 401,
    CREDENCIALES_INVALIDAS: 401,
    CUENTA_BLOQUEADA: 423,
    PERMISO_DENEGADO: 403,
    DISPOSITIVO_NO_AUTORIZADO: 403,
    SESION_OFFLINE_EXPIRADA: 401,
    NO_ENCONTRADO: 404,
    DATOS_INVALIDOS: 422,
    CONFLICTO_VERSION: 409,
    CONFLICTO_REQUIERE_REVISION: 409,
    CLAVE_IDEMPOTENTE_REUTILIZADA: 409,
    DEMASIADAS_SOLICITUDES: 429,
    PAYLOAD_DEMASIADO_GRANDE: 413,
    ERROR_INTERNO: 500,
  });

/** Codigo de dominio -> estado HTTP. Por defecto 422 (regla de negocio incumplida). */
export function httpStatusForCode(code: string): number {
  return HTTP_STATUS_BY_CODE[code] ?? 422;
}

// ---------------------------------------------------------------------------
// Paginacion por cursor
// ---------------------------------------------------------------------------

/**
 * Paginacion por cursor (no por offset): es estable ante inserciones concurrentes
 * y evita escaneos completos, lo que importa con los limites de filas leidas de D1.
 */
export const paginationQuerySchema = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export function pageSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    /** Cursor para la pagina siguiente. `null` cuando ya no hay mas. */
    nextCursor: z.string().nullable(),
    /** Instante en que el servidor produjo la pagina (fecha de corte del reporte). */
    generatedAt: instantSchema,
  });
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
  generatedAt: string;
}

// ---------------------------------------------------------------------------
// Idempotencia y concurrencia optimista
// ---------------------------------------------------------------------------

export const IDEMPOTENCY_HEADER = "idempotency-key";
export const CONTRACT_VERSION_HEADER = "x-contract-version";
export const CORRELATION_HEADER = "x-correlation-id";
export const INSTALLATION_HEADER = "x-installation-id";

export const idempotencyKeySchema = z.string().min(8).max(200);

/** Control de concurrencia optimista: version entera por entidad. */
export const versionedSchema = z.object({
  version: z.number().int().nonnegative(),
});

// ---------------------------------------------------------------------------
// Limites de tamano
// ---------------------------------------------------------------------------

/** Tamano maximo de un cuerpo JSON aceptado por la API. */
export const MAX_BODY_BYTES = 256 * 1024;
/** Maximo de operaciones por lote de sincronizacion. */
export const MAX_SYNC_BATCH = 50;
/** Maximo de filas exportables en una sola peticion. */
export const MAX_EXPORT_ROWS = 5000;

/**
 * Tope de estudiantes que se pueden pedir en un mismo reporte.
 *
 * El límite no es de cálculo sino de URL: los identificadores viajan en la cadena
 * de consulta separados por comas, y pasado este número se supera el largo que
 * algunos servidores intermedios aceptan. Para grupos mayores está el filtro por
 * grado o sección, que no necesita enumerar a nadie.
 */
export const MAX_REPORT_STUDENTS = 50;
